import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DashboardLayout = ({ children, userInfo, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: '📊', current: location.pathname === '/dashboard' || location.pathname === '/dashboard/' },
    { name: 'Subaccounts', href: '/dashboard/subaccounts', icon: '🏢', current: location.pathname === '/dashboard/subaccounts' },
    { name: 'Settings', href: '/dashboard/settings', icon: '⚙️', current: location.pathname === '/dashboard/settings' },
    { name: 'Billing', href: '/dashboard/billing', icon: '💳', current: location.pathname === '/dashboard/billing' },
  ];

  const handleNavigation = (href) => {
    // Use React Router navigation instead of window.location
    if (href.startsWith('http')) {
      window.open(href, '_blank');
    } else if (href.startsWith('/dashboard/')) {
      navigate(href);
    } else {
      window.location.href = href;
    }
  };  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <span className="h-6 w-6 text-white">✕</span>
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <div className="flex items-center space-x-2">
                  <img src="https://admin.gowa.app/WAGo_logo.svg" alt="GOWa Logo" className="w-6 h-6" />
                  <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">GOWa</h1>
                </div>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {navigation.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item.href)}
                    className={`flex items-center px-2 py-2 text-sm font-medium rounded-lg w-full text-left transition-all duration-200 ${
                      item.current
                        ? 'bg-green-50 border-green-500 text-green-700 shadow-sm'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {item.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="flex items-center space-x-2">
                  <img src="https://admin.gowa.app/WAGo_logo.svg" alt="GOWa Logo" className="w-8 h-8" />
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">GOWa</h1>
                </div>
              </div>
              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {navigation.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item.href)}
                    className={`flex items-center px-2 py-2 text-sm font-medium rounded-lg w-full text-left transition-all duration-200 border-l-4 ${
                      item.current
                        ? 'bg-green-50 border-green-500 text-green-700 shadow-sm'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {item.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <span className="h-6 w-6">☰</span>
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              {/* Page title area */}
              <h1 className="text-lg font-semibold text-gray-900">GOWa Dashboard</h1>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              {/* Notifications */}
              <button className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <span className="sr-only">View notifications</span>
                <span className="h-6 w-6">🔔</span>
              </button>

              {/* Profile dropdown */}
              <div className="ml-3 relative">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {userInfo?.user?.firstName ? userInfo.user.firstName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-700">
                      {userInfo?.user?.firstName ? `${userInfo.user.firstName} ${userInfo.user.lastName}` : userInfo?.user?.email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {userInfo?.agency?.name}
                    </div>
                  </div>
                  <button
                    onClick={onLogout}
                    className="ml-3 btn-danger text-sm px-3 py-1"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;